import React, { useState } from 'react';
import { Button, Grid, Paper, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import ErrorIcon from '@material-ui/icons/Error';
import { saveVendor } from './npwp-collector-service';
import { formatNpwp } from '../efaktur-download-page/services/download.service';

const useStyles = makeStyles(() => ({
  paperStyle: {
    maxWidth: '1140px',
    maxHeight: '612px'
  },
  leftPane: {
    paddingTop: '40px'
  },
  formTypeButton: {
    width: '50%'
  },
  dialog: {
    content: {
      height: '281px',
      width: '530px',
      borderRadius: '5px',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 -2px 8px 5px rgba(0,0,0,0.04)'
    }
  }
}));

const SMART_UPLOAD = 'SMART_UPLOAD';
const FILL_FORM = 'FILL_FORM';

const NPWP_FIELD = 'npwp';
const NAME_FIELD = 'name';
const ADDRESS_FIELD = 'address';
const NPWP_FILE_FIELD = 'npwp-file';

function isBlank(str) {
  return (!str || str.trim().length === 0);
}

function noErrors() {
  return new Map()
    .set(NPWP_FIELD, false)
    .set(NAME_FIELD, false)
    .set(ADDRESS_FIELD, false)
    .set(NPWP_FILE_FIELD, false);
}

function NpwpCollectorPage() {
  const { vendorEmail, vendorToken, opBuyerCompanyId } = useParams();
  const classes = useStyles();

  const [activeForm, setActiveForm] = useState(SMART_UPLOAD);
  const [errors, setErrors] = useState(noErrors());

  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState(<></>);

  const [name, setName] = useState('');
  const [npwp, setNpwp] = useState('');
  const [address, setAddress] = useState('');
  const [npwpFile, setNpwpFile] = useState(null);

  const [vendorInputFormat, setVendorInputFormat] = React.useState('number');

  const updateError = (fieldId, isFieldInvalid) => {
    setErrors(new Map(errors.set(fieldId, isFieldInvalid)));
  };

  const removeErrorField = (fieldId) => {
    errors.delete(fieldId);
    setErrors(new Map(errors));
  };

  const handleNameChange = (event) => {
    updateError(NAME_FIELD, isBlank(event.target.value));
    setName(event.target.value);
  };

  const handleAddressChange = (event) => {
    updateError(ADDRESS_FIELD, isBlank(event.target.value));
    setAddress(event.target.value);
  };

  const handleNPwpFileChange = (event) => {
    const isFileInvalid = !event.target.files || event.target.files.length === 0;
    updateError(ADDRESS_FIELD, isFileInvalid);
    setNpwpFile(event.target.files[0]);
  };

  const handleNpwpChange = (event) => {
    updateError(NPWP_FIELD, isBlank(event.target.value));

    const input = formatNpwp(event.target.value);
    const isInvalid = input.length < 20;

    if (isInvalid) { setVendorInputFormat('number'); } else { setVendorInputFormat('text'); }
    setNpwp(input);
  };

  const handleSmartUploadClick = () => {
    setActiveForm(SMART_UPLOAD);
    updateError(NPWP_FILE_FIELD, false);
  };

  const handleFillFormClick = () => {
    setActiveForm(FILL_FORM);
    removeErrorField(NPWP_FILE_FIELD);
  };

  const isFormValid = () => {
    const fieldValidations = [isBlank(npwp), isBlank(name), isBlank(address)];

    if (activeForm === SMART_UPLOAD) {
      fieldValidations.push(!npwpFile);
    }

    return [...fieldValidations].reduce((acc, isFieldInvalid) => acc || isFieldInvalid, false);
  };

  const isSubmitDisabled = isFormValid();
  const isSmartUploadActive = activeForm === SMART_UPLOAD;
  const isFillFormActive = activeForm === FILL_FORM;

  const handleClose = () => setShowDialog(false);

  const successContent = (
    <>
      <img src="/success.svg" alt="success" />
      <div>Successfully sent NPWP credentials!</div>
    </>
  );

  const errorContent = (
    <>
      <ErrorIcon color="primary" />
      <div>Submit Failed !</div>
    </>
  );

  const displayDialog = async (content) => {
    setDialogContent(content);
    setShowDialog(true);
  };

  const clearForm = () => {
    setNpwp('');
    setNpwpFile(null);
    setName('');
    setAddress('');
  };

  const submitVendor = async () => {
    await saveVendor({
      name,
      email: vendorEmail,
      npwp,
      address,
      npwpFile,
      token: vendorToken,
      opBuyerCompanyId
    }, () => {
      clearForm();
      displayDialog(successContent);
    },
    () => displayDialog(errorContent));
  };

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={handleClose}
        className={classes.dialog}
      >
        <DialogContent>
          {dialogContent}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Paper className={classes.paperStyle} elevation={0} variant="outlined">
        <Grid container={true}>
          <Grid item={true} xs={6}>
            <img
              src="/npwpSmartUpload.png"
              alt="Please upload file or simply put your NPWP along with credential information to submit NPWP."
            />
          </Grid>
          <Grid container={true} item={true} xs={6} spacing={3} className={classes.leftPane} justify="center">
            <Grid item={true} xs={12}>
              <Button
                size="large"
                className={classes.formTypeButton}
                variant={isSmartUploadActive ? 'contained' : 'outlined'}
                onClick={handleSmartUploadClick}
                disabled={isSmartUploadActive}
              >
                Smart Upload
              </Button>
              <Button
                size="large"
                className={classes.formTypeButton}
                variant={isFillFormActive ? 'contained' : 'outlined'}
                onClick={handleFillFormClick}
                disabled={isFillFormActive}
              >
                Fill Form
              </Button>
            </Grid>
            <Grid item={true} xs={12}>
              <TextField
                id="email"
                type="text"
                disabled={true}
                fullWidth={true}
                variant="outlined"
                InputLabelProps={{
                  shrink: true
                }}
                label="Tax Payer Email"
                value={vendorEmail}
              />
            </Grid>
            {activeForm === SMART_UPLOAD
            && (
              <Grid item={true} xs={12}>
                <TextField
                  id={NPWP_FILE_FIELD}
                  error={errors.get(NPWP_FILE_FIELD)}
                  type="file"
                  fullWidth={true}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="NPWP Image"
                  placeholder="Please Input Your Client's NPWP"
                  onChange={handleNPwpFileChange}
                />
              </Grid>
            )}
            <Grid item={true} xs={12}>
              <TextField
                id={NPWP_FIELD}
                error={errors.get(NPWP_FIELD)}
                type={vendorInputFormat}
                inputProps={{ maxLength: 20 }}
                fullWidth={true}
                variant="outlined"
                InputLabelProps={{
                  shrink: true
                }}
                label="NPWP"
                placeholder="Please input your NPWP here"
                onChange={handleNpwpChange}
                helperText={errors.get(NPWP_FIELD) ? 'Please input a valid npwp' : ''}
                value={npwp}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextField
                id={NAME_FIELD}
                error={errors.get(NAME_FIELD)}
                type="text"
                fullWidth={true}
                variant="outlined"
                InputLabelProps={{
                  shrink: true
                }}
                label="Tax Payer Name"
                placeholder="Please input your name here"
                onChange={handleNameChange}
                value={name}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <TextField
                id={ADDRESS_FIELD}
                error={errors.get(ADDRESS_FIELD)}
                fullWidth={true}
                variant="outlined"
                InputLabelProps={{
                  shrink: true
                }}
                multiline={true}
                label="Tax Payer Address"
                placeholder="Please input your address here"
                onChange={handleAddressChange}
                value={address}
              />
            </Grid>
            <Grid item={true} xs={12}>
              <Button
                variant="contained"
                disabled={isSubmitDisabled}
                disableElevation={true}
                fullWidth={true}
                color="primary"
                onClick={submitVendor}
              >
                SUBMIT
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}

export default NpwpCollectorPage;
